import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useParams } from 'react-router-dom';

export interface PillarComparisonData {
  time_series_data: Array<{
    date: string;
    post_volume: number;
    total_engagement: number;
  }>;
  metrics: {
    post_volume: number;
    total_engagement: number;
    average_sentiment: number | null; // Will be null until implemented
  };
}

export interface PillarComparisonParams {
  brand?: string;
  pillar?: string;
  startDate?: string | null;
  endDate?: string | null;
  platforms?: string[];
  orgId?: number;
}

export function usePillarComparisonData(params: PillarComparisonParams = {}) {
  const axiosClient = useAxiosClient();
  const { orgId: orgIdFromParams } = useParams<{ orgId: string }>();
  
  // Use orgId from params if provided, otherwise use from route params
  const orgId = params.orgId || parseInt(orgIdFromParams || '0', 10);
  const { brand, pillar, startDate, endDate, platforms } = params;

  // Build the query parameters
  const queryParams = new URLSearchParams();
  if (orgId) queryParams.append('org', orgId.toString());
  if (brand) queryParams.append('brand', brand);
  if (pillar) queryParams.append('pillar', pillar);
  if (startDate) queryParams.append('start_date', startDate);
  if (endDate) queryParams.append('end_date', endDate);
  
  // Add platforms as multiple parameters if provided
  if (platforms && platforms.length > 0) {
    platforms.forEach(platform => {
      queryParams.append('platforms', platform);
    });
  }

  // Create a query key that includes all parameters
  const queryKey = ['pillarComparisonData', orgId, brand, pillar, startDate, endDate, platforms];

  const {
    data,
    isLoading,
    error,
  } = useQuery(queryKey, async () => {
    if (!orgId) {
      throw new Error('No organization selected');
    }
    
    console.log("Fetching pillar comparison data with params:", {
      orgId,
      brand,
      pillar,
      startDate,
      endDate,
      platforms
    });
    
    try {
      const response = await axiosClient.get<PillarComparisonData>(
        `/api/get-pillar-comparison-data?${queryParams.toString()}`
      );

      console.log("Raw API response:", response);
      
      // If the response has an 'error' field, throw an error
      if ('error' in response.data) {
        console.error("API returned error:", response.data.error);
        throw new Error(
          typeof response.data.error === 'string'
            ? response.data.error
            : 'An error occurred fetching pillar comparison data'
        );
      }
      
      // Validate the response structure
      if (!response.data.time_series_data) {
        console.error("API response missing time_series_data:", response.data);
        throw new Error('Invalid API response: time_series_data is missing');
      }
      
      console.log("Processed API response:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching pillar comparison data:", err);
      throw err;
    }
  }, {
    // Add a staleTime to prevent unnecessary refetches
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Disable automatic refetching when window is focused
    refetchOnWindowFocus: false,
    // Only retry once if there's an error
    retry: 1,
    // Handle null/undefined data
    select: (data) => data ?? null,
    // Ensure we don't fetch if brand or pillar is not provided
    enabled: !!orgId && !!brand && !!pillar,
  });

  return {
    data: data ?? null,
    loading: isLoading,
    isLoading,
    error: error ? (error as Error).message : null,
  };
} 